import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"
];

const AvailabilityCalendar = ({ workerId, editable = false }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, year, month]);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/availability/${workerId}/month/${year}/${month + 1}`);
      setAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the 1st
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getDateString = (day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getAvailabilityForDay = (day) => {
    if (!day) return null;
    const dateStr = getDateString(day);
    return availability.find(a => a.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const isPast = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(year, month, day);
    return checkDate < today;
  };

  const handleDayClick = (day) => {
    if (!editable || !day || isPast(day)) return;
    
    const dateStr = getDateString(day);
    const existing = availability.find(a => a.date === dateStr);
    
    setSelectedDate(dateStr);
    setSelectedSlots(existing?.time_slots || []);
    setNotes(existing?.notes || "");
    setShowModal(true);
  };

  const toggleSlot = (slot) => {
    setSelectedSlots(prev => 
      prev.includes(slot) 
        ? prev.filter(s => s !== slot)
        : [...prev, slot].sort()
    );
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      if (selectedSlots.length === 0) {
        // Delete availability if no slots selected
        await axios.delete(`${API}/availability/${workerId}/${selectedDate}`);
        toast.success("Availability cleared");
      } else {
        await axios.post(`${API}/availability`, {
          worker_id: workerId,
          date: selectedDate,
          time_slots: selectedSlots,
          is_available: true,
          notes: notes || null
        });
        toast.success("Availability saved");
      }
      setShowModal(false);
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  return (
    <div className="bg-white rounded-2xl border border-[#dadce0] overflow-hidden" data-testid="availability-calendar">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#dadce0]">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h3 className="font-medium text-[#202124]">
          {monthNames[month]} {year}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
          data-testid="next-month-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div key={day} className="text-center text-xs font-medium text-[#5f6368] py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#1a73e8]" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {getDaysInMonth().map((day, idx) => {
              const dayAvail = getAvailabilityForDay(day);
              const hasSlots = dayAvail?.time_slots?.length > 0;
              const past = day && isPast(day);
              
              return (
                <div
                  key={idx}
                  onClick={() => handleDayClick(day)}
                  className={`
                    aspect-square p-1 rounded-lg text-center relative
                    ${day ? (editable && !past ? 'cursor-pointer hover:bg-[#f8f9fa]' : '') : ''}
                    ${isToday(day) ? 'ring-2 ring-[#1a73e8]' : ''}
                    ${past ? 'opacity-40' : ''}
                    ${hasSlots ? 'bg-[#e6f4ea]' : ''}
                  `}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <span className={`text-sm ${isToday(day) ? 'font-bold text-[#1a73e8]' : 'text-[#202124]'}`}>
                        {day}
                      </span>
                      {hasSlots && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                          <div className="flex gap-0.5">
                            {dayAvail.time_slots.slice(0, 3).map((_, i) => (
                              <div key={i} className="w-1 h-1 rounded-full bg-[#1e8e3e]" />
                            ))}
                            {dayAvail.time_slots.length > 3 && (
                              <span className="text-[8px] text-[#1e8e3e]">+</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 pb-4 flex items-center gap-4 text-xs text-[#5f6368]">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-[#e6f4ea]" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded ring-2 ring-[#1a73e8]" />
          <span>Today</span>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#1a73e8]" />
              Set Availability - {selectedDate}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Select Available Time Slots</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {TIME_SLOTS.map(slot => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => toggleSlot(slot)}
                    className={`px-2 py-2 text-sm rounded-lg border transition-colors ${
                      selectedSlots.includes(slot)
                        ? 'bg-[#1a73e8] text-white border-[#1a73e8]'
                        : 'bg-white text-[#5f6368] border-[#dadce0] hover:border-[#1a73e8]'
                    }`}
                    data-testid={`time-slot-${slot}`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-sm">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Morning appointments only..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveAvailability}
              disabled={saving}
              className="bg-[#1a73e8] hover:bg-[#1557b0]"
              data-testid="save-availability-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvailabilityCalendar;
